import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import gql from "graphql-tag";
import { Apollo } from "apollo-angular";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { EnvironmentService } from "../../snatch/services";
import { SessionService } from "../../snatch/services/session.service";
import { AmberResponse } from "../../snatch/models/amber-response.model";
import {
  ListProductVariantDeliverableModel
} from "../models/product-variant-deliverable.model";
import { Product } from "../models/product.model";
import { RelatedProductListingQuery } from "../../common/common-product/models/related-product-listing-query.model";
import { ISearchParams } from "../../snatch/models/products-search-params.model";
import { Sort } from "../../snatch/models/sort.model";
import { IPageable } from "../../snatch/models/page.model";
import {BundleModel} from "../models/bundle.model";

const GET_PRODUCT = gql`
    query Product($sku: String!) {
        details: readProduct(sku: $sku) {
            id,
            name,
            sku,
            countryOfOrigin,
            shortDescription,
            price,
            currency,
            longDescription,
            suffix,
            bundle {
                sku
                name
                description
                licensePricing {
                    licenseType
                    duration
                    durationType
                    sku
                    currency
                    price
                    licenseTypeValue
                }
             }
            courseDeliveryFormatsConfig {
                configValue
                configKey
            },
            levelConfig {
                configValue
                configKey
            },
            enabledForLxp,
            publishedToLxp,
            accessDuration,
            accessDurationType {
                configKey,
                configType,
                configValue
            },
            typeConfig {
                configValue
                configKey
            },
            seller {
                displayName,
                logo,
                accountId,
                name
            }
            pricing {
                amount,
                currency
            }
            details {
                ipStatement,
                sellerStatement,
                courseInfo {
                    agenda,
                    description,
                    outline,
                    learningObjectives,
                    targetAudience,
                    duration,
                    durationType,
                    prerequisites
                },
                examInfo {
                    extraTime,
                    fulfillment,
                    planning,
                    questions,
                    openBookExam,
                    passGrade,
                    passGradeInstructors,
                    questionFormat,
                    certificateAvailable,
                    duration,
                    durationType
                },
                instructions {
                    prerequisites,
                    examLocations,
                    openBookExamRequirements,
                    instructorRequirements,
                    technicalRequirements
                },
                productSegment {
                    code,
                    name,
                    sellerRole,
                    sellerAccreditationStatus,
                    trainerRequirements,
                    restrictionMessage,
                    accreditationRequirements,
                    accreditationProcess,
                    examInformation
                }
            },
            render {
                mediumImageUri
            },
            productVariants {
                id,
                sku,
                name,
                bundleLicensePricingSKU
                defaultVariant,
                renders {
                    mediumImageUri
                },
                delivery {
                    freeReturn30Day,
                    requiresVoucher,
                    accessDuration,
                    accessDurationType,
                    freightClassId,
                    manualFulfillment,
                    surcharge,
                    quotation,
                    pageCount,
                    deliverables,
                    printedDeliverables,
                    evaluationCopyAvailable
                },
                pricing {
                    currency,
                    subtotal,
                    surchargeTotalAmount,
                    surcharges {
                        key,
                        name,
                        amount
                    },
                    basePrice,
                    actualBasePrice,
                    promotionPricingDetail {
                        promotionPricingDiscount,
                        promotionPricingPercentage,
                        promotionPricingDiscountEndDate
                    }
                    isPromotionPricingApplied
                },
                shipping {
                    id,
                    name,
                    shipsWithin,
                    arrivesWithin,
                    shipsToSelectedAddress
                },
                languageConfig {
                    configValue
                    configKey
                },
                deliveryFormatConfig {
                    configValue
                    configKey
                },
                certificateTypeConfig {
                    configValue
                    configKey
                },
                language,
                deliveryFormat,
                certificateType
            }
            reviews {
                id,
                message,
                rating,
                anonymous,
                reviewedAt,
                user {
                    id,
                    firstName,
                    lastName,
                    account {
                        buyer,
                        seller,
                        instructor
                    }
                }
            },
            restriction {
                restricted,
                hasAccess,
                isNotContractedSupplier,
                accreditationSegments {
                    code,
                    name
                }
            },
            promotionalMaterials {
                url,
                title,
                description
            }, 
            videos {
                url,
                title,
                description,
                featured
            },
            countryOfOrigin
        }
    }
`;

const COMPUTE_PRODUCT_PRICE = gql`
    query computeProductPrice($pricing: ProductPriceQuery!) {
        results: computeProductPrice(pricing: $pricing) {
            currency,
            surchargeTotalAmount,
            surcharge,
            subtotal,
            surcharges {
                amount,
                key,
                name
            },
            total,
            basePrice
        }
    }
`;

const LIST_RELATED_PRODUCTS = gql`
    query listRelatedProducts($listing: RelatedProductListingQuery!) {
        results: listRelatedProducts(listing: $listing) {
            products {
                id,
                name,
                sku,
                type,
                shortDescription,
                typeConfig {
                    configValue
                    configValue
                },
                pricing {
                    amount,
                    currency
                },
                render {
                    mediumImageUri
                },
                seller {
                    displayName,
                    logo
                }
                productVariants {
                    id,
                    sku,
                    name,
                    language,
                    deliveryFormat,
                    certificateType,
                    defaultVariant,
                    renders {
                        mediumImageUri
                    },
                    languageConfig {
                        configValue
                        configKey
                    },
                    deliveryFormatConfig {
                        configValue
                        configKey
                    },
                    certificateTypeConfig {
                        configValue
                        configKey
                    }
                },
                restriction {
                    restricted,
                    hasAccess,
                    isNotContractedSupplier,
                    accreditationSegments {
                        code,
                        name
                    }
                }
            }
        }
    }
`;


const LIST_ORDER_TEMPLATES_MINIMAL = gql`
    query listOrderTemplates($listing: OrderTemplateListingQuery!) {
        listOrderTemplates(listing: $listing) {
            templates {
                name
                id
                items {
                    id
                    quantity
                    sku
                }
            }
            totalCount

        }
    }
`;

const LIST_BUNDLE_PRODUCTS = gql`
    query listBundleProducts($query: BundleProductsListQuery!) {
        results: listBundleProducts(query: $query) {
          totalProducts
          products {
                id
                name
                sku
                typeConfig {
                    configValue
                    configKey
                }
                levelConfig {
                    configValue
                    configKey
                }
                courseDeliveryFormatsConfig {
                    configValue
                    configKey
                }
                shortDescription
                pricing {
                    amount
                    currency
                    actualAmount
                    isPromotionPricingApplied
                    promotionPricingDetail {
                        promotionPricingDiscount
                        promotionPricingPercentage
                        promotionPricingDiscountEndDate
                    }
                }
                suffix
                render {
                    mediumImageUri
                }
                seller {
                    displayName
                    name
                    logo
                }
                productVariants {
                    id
                    sku
                    name
                    language
                    deliveryFormat
                    certificateType
                    defaultVariant
                    renders {
                        mediumImageUri
                    }
                    languageConfig {
                        configValue
                        configKey
                    }
                    deliveryFormatConfig {
                        configValue
                        configKey
                    }
                    certificateTypeConfig {
                        configValue
                        configKey
                    }
                    language
                    deliveryFormat
                    certificateType
                }
                restriction {
                      restricted
                      hasAccess
                      isNotContractedSupplier
                      accreditationSegments {
                          code
                          name
                      }
                }
          publishedToLxp
          enabledForLxp
        }
      }
    }
`;

@Injectable()
export class ProductDetailsService {
  constructor(
    private apollo: Apollo,
    private environmentService: EnvironmentService,
    private sessionService: SessionService,
    private http: HttpClient
  ) {
  }


  getProduct(sku: string): Observable<any> {
    return this.apollo
      .watchQuery({
        query: GET_PRODUCT,
        variables: {
          sku: sku
        }
      })
      .valueChanges;
  }

  listRelatedProducts(sku: string, limit?: number): Observable<any> {
    let listing: RelatedProductListingQuery = {
      limit: limit || 12,
      sku: sku
    };

    return this.apollo
      .watchQuery({
        query: LIST_RELATED_PRODUCTS,
        variables: { listing }
      })
      .valueChanges;
  }

  getProductVariantDeliverables(productVariantSku: string) {
    const url = this.environmentService.amberUrl +
      "/library/blended/deliverable/bulk?sku=" +  productVariantSku;
    const params = new HttpParams();
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      "Authorization": `Bearer ${this.sessionService.getToken()}`
    });

    return this.http.get<AmberResponse<ListProductVariantDeliverableModel>>(url, { params: params, headers: headers });
  }

  requestEvaluationCopy(productVariantSku: string, userEmail) {
    const url = this.environmentService.amberUrl +
      "/voucher/evaluation/copy/create";
    const params = new HttpParams();
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      "Authorization": `Bearer ${this.sessionService.getToken()}`
    });
    const payload = { sku: productVariantSku, userEmail: userEmail };

    return this.http.post<AmberResponse<any>>(url, payload, { params: params, headers: headers });
  }

  checkSegments(segments: string[]) {
    const url = this.environmentService.amberUrl +
      "/phoenix-v2/contract/segments/check";
    const params = new HttpParams();
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      "Authorization": `Bearer ${this.sessionService.getToken()}`
    });

    return this.http.post<AmberResponse<any>>(url, segments, { params: params, headers: headers });
  }

  computeProductPrice(sku: string, quantity: number) {
    return this.apollo
      .watchQuery({
        query: COMPUTE_PRODUCT_PRICE,
        variables: {
          pricing: { sku, quantity }
        }
      })
      .valueChanges;
  }

  isCourseInformationAvailable(productDetails: Product) {
    const courseInfo = productDetails && productDetails.details && productDetails.details.courseInfo;
    return courseInfo && (
      courseInfo.description ||
      courseInfo.outline ||
      courseInfo.targetAudience ||
      courseInfo.learningObjectives ||
      courseInfo.agenda
    );

  }

  listMinimal(filters: ISearchParams = {}, sort: Sort = {}, pageable?: IPageable): Observable<any> {

    let listing: any = {};

    listing.page = pageable ? pageable.page : 1;
    listing.limit = pageable ? pageable.limit : 100;
    listing.sortField = sort.key || "name";
    listing.sortDir = sort.order;
    listing.name = filters.filter;


    return this.apollo
      .query({
        query: LIST_ORDER_TEMPLATES_MINIMAL,
        variables: { listing }
      });
  }

  getProductRelatedBundles(productSku: string, pageable: IPageable) {
    const url = this.environmentService.amberUrl +
        "/agora/bundle/all-domains/list-filters";

    const params = new HttpParams()
        .append('productSku', productSku)
        .append('limit', pageable.limit.toString())
        .append('page', pageable.page.toString())
    ;
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      "Authorization": `Bearer ${this.sessionService.getToken()}`
    });

    return this.http.get<AmberResponse<BundleModel[]>>(url, { params: params, headers: headers });
  }

  getIncludedProducts(bundleSku: string, pageable: IPageable): Observable<any> {
    let query = {
      limit: pageable.limit,
      page: pageable.page,
      bundleSku
    };

    return this.apollo
        .watchQuery({
          query: LIST_BUNDLE_PRODUCTS,
          variables: { query }
        })
        .valueChanges;
  }
}
